import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Plane,
  Package,
  Check,
  X,
  ChevronRight,
  Shield,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import {
  TRAVEL_MODULES,
  TRADING_MODULES,
  PERMISSION_ACTIONS,
  TRAVEL_MODULE_LABELS,
  TRADING_MODULE_LABELS,
  PERMISSION_ACTION_LABELS,
  type AppType,
  type PermissionAction,
} from '@/stores/app-store';

export function UserPermissionsPage() {
  const { t: _t } = useTranslation('settings');
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Fetch users with access info
  const { data: users, isLoading } = trpc.permission.listUsersWithAccess.useQuery({
    search: search || undefined,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            User Permissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage app access and module permissions for users
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-full border border-border bg-background pl-10 pr-4 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
        />
      </div>

      {/* Users List */}
      <div className="border border-border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !users?.length ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No users found
          </div>
        ) : (
          <div className="divide-y divide-border">
            {users.map((user) => (
              <div
                key={user.id}
                className="flex cursor-pointer items-center justify-between p-3 transition-colors hover:bg-muted/50"
                onClick={() => setSelectedUserId(user.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center bg-primary/10 text-xs font-medium text-primary">
                    {user.firstName[0]}
                    {user.lastName[0]}
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {/* Role Badge */}
                  <span className="bg-muted px-2 py-0.5 text-xs font-medium">
                    {user.role}
                  </span>

                  {/* App Access Badges */}
                  <div className="flex items-center gap-1">
                    {user.apps.includes('TRAVEL') && (
                      <span className="flex items-center gap-1 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                        <Plane className="h-3 w-3" />
                        Travel
                      </span>
                    )}
                    {user.apps.includes('TRADING') && (
                      <span className="flex items-center gap-1 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                        <Package className="h-3 w-3" />
                        Trading
                      </span>
                    )}
                    {user.apps.length === 0 && user.role !== 'SUPER_ADMIN' && (
                      <span className="bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        No Access
                      </span>
                    )}
                  </div>

                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Permission Editor Modal */}
      {selectedUserId && (
        <PermissionEditorModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}

interface PermissionEditorModalProps {
  userId: string;
  onClose: () => void;
}

function PermissionEditorModal({ userId, onClose }: PermissionEditorModalProps) {
  const [activeTab, setActiveTab] = useState<'apps' | 'travel' | 'trading'>('apps');
  const utils = trpc.useUtils();

  // Fetch user permissions
  const { data: userData, isLoading } = trpc.permission.getUserPermissions.useQuery(userId);

  // Mutations
  const updateAppAccessMutation = trpc.permission.updateUserAppAccess.useMutation({
    onSuccess: () => {
      utils.permission.getUserPermissions.invalidate(userId);
      utils.permission.listUsersWithAccess.invalidate();
    },
  });

  const updateModulePermsMutation = trpc.permission.updateUserModulePermissions.useMutation({
    onSuccess: () => {
      utils.permission.getUserPermissions.invalidate(userId);
    },
  });

  const applyTemplateMutation = trpc.permission.applyRoleTemplate.useMutation({
    onSuccess: () => {
      utils.permission.getUserPermissions.invalidate(userId);
    },
  });

  if (isLoading || !userData) {
    return (
      <Dialog open onOpenChange={() => onClose()}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const { user, apps, modules, isSuperAdmin } = userData;

  // Handle app access toggle
  const handleAppToggle = (app: AppType) => {
    const currentApps = apps.map((a) => a.app);
    let newApps: { app: AppType; isDefault: boolean }[];

    if (currentApps.includes(app)) {
      // Remove app
      newApps = apps.filter((a) => a.app !== app);
      // If removing the default app, make first remaining one default
      if (apps.find((a) => a.app === app)?.isDefault && newApps.length > 0 && newApps[0]) {
        newApps[0].isDefault = true;
      }
    } else {
      // Add app
      newApps = [...apps, { app, isDefault: apps.length === 0 }];
    }

    updateAppAccessMutation.mutate({
      userId,
      apps: newApps,
    });
  };

  // Handle default app change
  const handleDefaultAppChange = (app: AppType) => {
    const newApps = apps.map((a) => ({
      app: a.app,
      isDefault: a.app === app,
    }));

    updateAppAccessMutation.mutate({
      userId,
      apps: newApps,
    });
  };

  // Apply role template
  const handleApplyTemplate = (template: string) => {
    applyTemplateMutation.mutate({
      userId,
      template: template as any,
      apps: apps.map((a) => a.app),
    });
  };

  const hasAppAccess = (app: AppType) => apps.some((a) => a.app === app);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center bg-primary/10 text-xs font-medium text-primary">
              {user.firstName[0]}
              {user.lastName[0]}
            </div>
            <div>
              <div className="text-sm">
                {user.firstName} {user.lastName}
              </div>
              <div className="text-xs font-normal text-muted-foreground">
                {user.email} • {user.role}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {isSuperAdmin ? (
          <div className="flex items-center justify-center py-12 text-center">
            <div>
              <Shield className="h-12 w-12 mx-auto text-primary mb-4" />
              <h3 className="font-medium">Super Admin</h3>
              <p className="text-sm text-muted-foreground mt-1">
                This user has full access to all apps and modules.
                <br />
                Super Admin permissions cannot be modified.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-1 border-b">
              <button
                onClick={() => setActiveTab('apps')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'apps'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                App Access
              </button>
              {hasAppAccess('TRAVEL') && (
                <button
                  onClick={() => setActiveTab('travel')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'travel'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Plane className="h-4 w-4" />
                    Travel Permissions
                  </span>
                </button>
              )}
              {hasAppAccess('TRADING') && (
                <button
                  onClick={() => setActiveTab('trading')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'trading'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Package className="h-4 w-4" />
                    Trading Permissions
                  </span>
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto py-4">
              {activeTab === 'apps' && (
                <div className="space-y-6">
                  <div className="grid gap-4">
                    {/* Travel App */}
                    <div
                      className={`flex items-center justify-between border-2 p-3 transition-colors ${
                        hasAppAccess('TRAVEL')
                          ? 'border-blue-200 bg-blue-50'
                          : 'border-muted'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 ${
                            hasAppAccess('TRAVEL')
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          <Plane className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Travel</p>
                          <p className="text-xs text-muted-foreground">
                            Travel agency management
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {hasAppAccess('TRAVEL') && (
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="radio"
                              name="defaultApp"
                              checked={apps.find((a) => a.app === 'TRAVEL')?.isDefault}
                              onChange={() => handleDefaultAppChange('TRAVEL')}
                              className="h-3 w-3"
                            />
                            Default
                          </label>
                        )}
                        <Button
                          variant={hasAppAccess('TRAVEL') ? 'default' : 'outline'}
                          size="sm"
                          className="h-8"
                          onClick={() => handleAppToggle('TRAVEL')}
                          disabled={updateAppAccessMutation.isPending}
                        >
                          {hasAppAccess('TRAVEL') ? (
                            <>
                              <Check className="mr-1 h-4 w-4" />
                              Enabled
                            </>
                          ) : (
                            'Enable'
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Trading App */}
                    <div
                      className={`flex items-center justify-between border-2 p-3 transition-colors ${
                        hasAppAccess('TRADING')
                          ? 'border-amber-200 bg-amber-50'
                          : 'border-muted'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 ${
                            hasAppAccess('TRADING')
                              ? 'bg-amber-100 text-amber-600'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          <Package className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Trading</p>
                          <p className="text-xs text-muted-foreground">
                            Product trading & inventory
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {hasAppAccess('TRADING') && (
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="radio"
                              name="defaultApp"
                              checked={apps.find((a) => a.app === 'TRADING')?.isDefault}
                              onChange={() => handleDefaultAppChange('TRADING')}
                              className="h-3 w-3"
                            />
                            Default
                          </label>
                        )}
                        <Button
                          variant={hasAppAccess('TRADING') ? 'default' : 'outline'}
                          size="sm"
                          className="h-8"
                          onClick={() => handleAppToggle('TRADING')}
                          disabled={updateAppAccessMutation.isPending}
                        >
                          {hasAppAccess('TRADING') ? (
                            <>
                              <Check className="mr-1 h-4 w-4" />
                              Enabled
                            </>
                          ) : (
                            'Enable'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Quick Templates */}
                  {apps.length > 0 && (
                    <div className="border-t border-border pt-4">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Apply Permission Template</p>
                      <div className="flex flex-wrap gap-1">
                        {['FULL_ACCESS', 'SALES_MANAGER', 'AGENT', 'ACCOUNTANT', 'OPERATIONS', 'VIEWER'].map(
                          (template) => (
                            <Button
                              key={template}
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => handleApplyTemplate(template)}
                              disabled={applyTemplateMutation.isPending}
                            >
                              {template.replace('_', ' ')}
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'travel' && hasAppAccess('TRAVEL') && (
                <PermissionMatrix
                  appType="TRAVEL"
                  modules={[...TRAVEL_MODULES]}
                  moduleLabels={TRAVEL_MODULE_LABELS}
                  currentPermissions={modules.TRAVEL || {}}
                  onSave={(perms) =>
                    updateModulePermsMutation.mutate({ userId, app: 'TRAVEL', permissions: perms })
                  }
                  isSaving={updateModulePermsMutation.isPending}
                />
              )}

              {activeTab === 'trading' && hasAppAccess('TRADING') && (
                <PermissionMatrix
                  appType="TRADING"
                  modules={[...TRADING_MODULES]}
                  moduleLabels={TRADING_MODULE_LABELS}
                  currentPermissions={modules.TRADING || {}}
                  onSave={(perms) =>
                    updateModulePermsMutation.mutate({ userId, app: 'TRADING', permissions: perms })
                  }
                  isSaving={updateModulePermsMutation.isPending}
                />
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface PermissionMatrixProps {
  appType: AppType;
  modules: readonly string[];
  moduleLabels: Record<string, string>;
  currentPermissions: Record<string, string[]>;
  onSave: (permissions: { module: string; actions: PermissionAction[] }[]) => void;
  isSaving: boolean;
}

function PermissionMatrix({
  modules,
  moduleLabels,
  currentPermissions,
  onSave,
  isSaving,
}: PermissionMatrixProps) {
  // Local state for batch editing
  const [localPerms, setLocalPerms] = useState<Record<string, Set<string>>>(() => {
    const initial: Record<string, Set<string>> = {};
    for (const m of modules) {
      initial[m] = new Set(currentPermissions[m] || []);
    }
    return initial;
  });

  // Track if there are unsaved changes
  const hasChanges = modules.some((m) => {
    const current = new Set(currentPermissions[m] || []);
    const local = localPerms[m] || new Set();
    if (current.size !== local.size) return true;
    for (const a of current) if (!local.has(a)) return true;
    return false;
  });

  const toggle = (module: string, action: string) => {
    setLocalPerms((prev) => {
      const next = { ...prev };
      const set = new Set(prev[module] || []);
      if (set.has(action)) set.delete(action); else set.add(action);
      next[module] = set;
      return next;
    });
  };

  const toggleRowAll = (module: string) => {
    setLocalPerms((prev) => {
      const next = { ...prev };
      const set = prev[module] || new Set();
      const allSelected = PERMISSION_ACTIONS.every((a) => set.has(a));
      next[module] = allSelected ? new Set() : new Set(PERMISSION_ACTIONS);
      return next;
    });
  };

  const toggleColumnAll = (action: string) => {
    setLocalPerms((prev) => {
      const allSelected = modules.every((m) => (prev[m] || new Set()).has(action));
      const next: Record<string, Set<string>> = {};
      for (const m of modules) {
        const set = new Set(prev[m] || []);
        if (allSelected) set.delete(action); else set.add(action);
        next[m] = set;
      }
      return next;
    });
  };

  const selectAll = () => {
    const next: Record<string, Set<string>> = {};
    for (const m of modules) next[m] = new Set(PERMISSION_ACTIONS);
    setLocalPerms(next);
  };

  const clearAll = () => {
    const next: Record<string, Set<string>> = {};
    for (const m of modules) next[m] = new Set();
    setLocalPerms(next);
  };

  const handleSave = () => {
    const perms = modules.map((m) => ({
      module: m,
      actions: Array.from(localPerms[m] || []) as PermissionAction[],
    }));
    onSave(perms);
  };

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          <button onClick={selectAll} className="rounded border border-border px-2 py-1 text-xs hover:bg-muted">
            Tümünü Seç
          </button>
          <button onClick={clearAll} className="rounded border border-border px-2 py-1 text-xs hover:bg-muted">
            Tümünü Kaldır
          </button>
        </div>
        {hasChanges && (
          <Button size="sm" className="h-7" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
            Kaydet
          </Button>
        )}
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">Module</th>
              {PERMISSION_ACTIONS.map((action) => {
                const allChecked = modules.every((m) => (localPerms[m] || new Set()).has(action));
                return (
                  <th key={action} className="px-2 py-2 text-center font-medium">
                    <button onClick={() => toggleColumnAll(action)} className="hover:text-primary" title={`Tümünü ${allChecked ? 'kaldır' : 'seç'}: ${PERMISSION_ACTION_LABELS[action]}`}>
                      {PERMISSION_ACTION_LABELS[action]}
                    </button>
                  </th>
                );
              })}
              <th className="px-2 py-2 text-center font-medium w-16">Tümü</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((module) => {
              const perms = localPerms[module] || new Set();
              const allSelected = PERMISSION_ACTIONS.every((a) => perms.has(a));
              return (
                <tr key={module} className="border-b border-border hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium">{moduleLabels[module]}</td>
                  {PERMISSION_ACTIONS.map((action) => {
                    const has = perms.has(action);
                    return (
                      <td key={action} className="px-2 py-2 text-center">
                        <button
                          onClick={() => toggle(module, action)}
                          className={`flex h-5 w-5 items-center justify-center mx-auto transition-colors ${
                            has
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                        >
                          {has ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <X className="h-2.5 w-2.5 text-muted-foreground" />
                          )}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-center">
                    <button
                      onClick={() => toggleRowAll(module)}
                      className={`rounded px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        allSelected
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {allSelected ? '✓ All' : 'All'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Unsaved indicator */}
      {hasChanges && (
        <p className="text-xs text-amber-600">Kaydedilmemiş değişiklikler var</p>
      )}
    </div>
  );
}
